import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HardDrive, History, MapPin, Sparkles } from 'lucide-react';
import { api } from '../../services/apiService';
import { hardwareApi, type DeviceOrderRow, type DeviceUnitRow } from '../../services/hardwareApi';
import { Kpi, Note } from './financeUi';
import { financeTotals, freeQuotaUsage, money, isBillingLocation, type LocationRow } from './financeFormat';
import PlanPanel from './PlanPanel';
import LocationsPanel from './LocationsPanel';
import DevicesPanel from './DevicesPanel';
import HistoryPanel from './HistoryPanel';
import type { PaymentEvent, PricePlan, Tenant } from '../../types';

/**
 * TENANT → FINANCE
 *
 * One tab, four panels (spec §9). The old `billing` tab and the money half of
 * `overview` fold into this; identity, contacts and status stayed in `overview`.
 *
 * Extracted here rather than grown inside `TenantProfile.tsx`, which was already
 * 2,632 lines before any of this arrived.
 */

export type FinancePanelId = 'plan' | 'locations' | 'devices' | 'history';

export const FINANCE_PANELS: FinancePanelId[] = ['plan', 'locations', 'devices', 'history'];

const PANEL_TABS: { id: FinancePanelId; label: string; icon: React.FC<any> }[] = [
  { id: 'plan', label: 'Plan', icon: Sparkles },
  { id: 'locations', label: 'Locations', icon: MapPin },
  { id: 'devices', label: 'Devices', icon: HardDrive },
  { id: 'history', label: 'History', icon: History },
];

interface Props {
  tenantId: string;
  tenant: Tenant | null;
  locations: LocationRow[];
  locationsLoading: boolean;
  billingEvents: PaymentEvent[];
  prices: PricePlan[];
  pricesLoading: boolean;
  panel: FinancePanelId;
  onPanelChange: (panel: FinancePanelId) => void;
  /** Reload the tenant record and its recorded billing events. */
  onReload: () => Promise<void> | void;
  onReloadLocations: () => Promise<void> | void;
  notify: (message: string) => void;
}

const TenantFinancePanels: React.FC<Props> = ({
  tenantId, tenant, locations, locationsLoading, billingEvents, prices, pricesLoading,
  panel, onPanelChange, onReload, onReloadLocations, notify,
}) => {
  const [skus, setSkus] = useState<any[]>([]);
  const [entitlements, setEntitlements] = useState<any | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [units, setUnits] = useState<DeviceUnitRow[]>([]);
  const [orders, setOrders] = useState<DeviceOrderRow[]>([]);
  const [models, setModels] = useState<Array<{ modelId: string; modelName: string }>>([]);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [hardwareLoading, setHardwareLoading] = useState(true);

  // -- catalog + entitlements ---------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, e] = await Promise.allSettled([
        api.billingCatalog.listSkus(),
        api.billingCatalog.getEntitlements(tenantId),
      ]);
      if (cancelled) return;
      setSkus(s.status === 'fulfilled' ? ((s.value as any)?.items ?? []) : []);
      setEntitlements(e.status === 'fulfilled' ? e.value : null);
      // EITHER read failing hides package money: without the catalog there are
      // no amounts, without the entitlements there is nothing to price. Both
      // make the Software and Monthly tiles render LOW, so both must say so —
      // a total that is quietly short is worse than no total.
      setCatalogError(
        s.status === 'rejected' && e.status === 'rejected'
          ? 'billing-service is unreachable — package lines are missing, so the Software and Monthly totals are understated.'
          : s.status === 'rejected'
            ? 'The billing catalog could not be read, so package amounts are missing and the Software and Monthly totals are understated.'
            : e.status === 'rejected'
              ? 'This tenant\'s entitlements could not be read, so package lines are not shown and the Software and Monthly totals are understated.'
              : null,
      );
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  // -- hardware -------------------------------------------------------------
  const loadHardware = useCallback(async () => {
    setHardwareLoading(true);
    const [u, o, m] = await Promise.allSettled([
      hardwareApi.listUnits({ tenantId }),
      hardwareApi.listOrders({ tenantId }),
      hardwareApi.listModels(),
    ]);
    setUnits(u.status === 'fulfilled' ? (u.value.items || []) : []);
    setOrders(o.status === 'fulfilled' ? (o.value.items || []) : []);
    setModels(m.status === 'fulfilled' ? (m.value.items || []) : []);
    // Units and orders are separate reads and fail separately, so they are
    // reported separately: an orders-only failure must not leave the device
    // list looking authoritative, and a units-only failure must not make the
    // orders list claim there are none. A missing MODEL list costs a pretty
    // name, not a number, so it is deliberately not an error.
    setUnitsError(
      u.status === 'rejected'
        ? (u.reason?.message || 'hardware-service is unreachable — devices and agreements are not shown.')
        : null,
    );
    setOrdersError(
      o.status === 'rejected'
        ? (o.reason?.message || 'hardware-service is unreachable — device orders are not shown.')
        : null,
    );
    setHardwareLoading(false);
  }, [tenantId]);
  useEffect(() => { loadHardware(); }, [loadHardware]);

  const modelNames = useMemo(
    () => Object.fromEntries(models.map((m) => [m.modelId, m.modelName])),
    [models],
  );

  /** Location names keyed by BOTH ids: units carry `locationId`, requests carry `requestId`. */
  const locationNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locations) {
      const name = l.name || l.requestId;
      if (l.locationId) map[l.locationId] = name;
      map[l.requestId] = name;
    }
    return map;
  }, [locations]);

  // -- totals ---------------------------------------------------------------
  const skuById = useMemo(() => Object.fromEntries(skus.map((s: any) => [s.skuId, s])), [skus]);
  const entRows: any[] = entitlements?.entitlements ?? [];
  const perStoreSkus = useMemo(
    () => entRows.map((e) => skuById[e.skuId]).filter((s: any) => s && s.scope === 'location'),
    [entRows, skuById],
  );
  const tenantSkus = useMemo(
    () => entRows.map((e) => skuById[e.skuId]).filter((s: any) => s && s.scope === 'tenant'),
    [entRows, skuById],
  );

  const billingLocationCount = useMemo(() => locations.filter(isBillingLocation).length, [locations]);
  const packageCents = useMemo(
    () => tenantSkus.reduce((sum: number, s: any) => sum + (s.amountCents || 0), 0)
      + perStoreSkus.reduce((sum: number, s: any) => sum + (s.amountCents || 0), 0) * billingLocationCount,
    [tenantSkus, perStoreSkus, billingLocationCount],
  );

  /**
   * The live Stripe price wins over the amount copied onto the tenant record:
   * the copy is a snapshot and goes stale if the plan is changed elsewhere. The
   * Plan panel's card reads it the same way, so the KPI and the card can never
   * disagree about what the base plan costs.
   */
  const basePlanCents = useMemo(() => {
    const price = prices.find((p) => p.id === tenant?.priceId);
    return price?.unitAmount ?? tenant?.subscriptionCost ?? 0;
  }, [prices, tenant?.priceId, tenant?.subscriptionCost]);

  const totals = useMemo(
    () => financeTotals({
      basePlanCents,
      locations,
      agreements: units.map((u) => u.agreement).filter(Boolean) as any[],
      packageCents,
    }),
    [basePlanCents, locations, units, packageCents],
  );
  const quota = freeQuotaUsage(locations, (tenant as any)?.freeLocationQuota);

  return (
    <div className="space-y-5 text-stone-800">
      <Note>
        <strong>Consolidated.</strong> The old <em>Billing</em> tab and the money half of <em>Overview</em> live here,
        together with per-location billing. Each location's own billing page stays reachable from the Locations panel.
      </Note>

      {/* KPIs ------------------------------------------------------------- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Monthly recurring"
          value={money(totals.totalCents)}
          sub={`${totals.billingLocations} location${totals.billingLocations === 1 ? '' : 's'} billing`}
        />
        <Kpi label="Software" value={money(totals.softwareCents)} sub="plan · locations · packages" />
        {/* A settled or waived agreement leaves this total straight away, but
            `agreementAction` does not touch Stripe — so the tile says how many
            it dropped rather than letting the fall read as "billing stopped". */}
        <Kpi
          label="Hardware"
          value={money(totals.hardwareCents)}
          sub={
            totals.closedAgreements
              ? `${totals.financedDevices} financed · ${totals.closedAgreements} closed, Stripe step pending`
              : totals.financedDevices
                ? `${totals.financedDevices} device${totals.financedDevices === 1 ? '' : 's'} financed`
                : 'nothing financed'
          }
          tone={totals.closedAgreements ? 'warn' : totals.hardwareCents ? 'default' : 'muted'}
        />
        <Kpi
          label="Free quota"
          value={String(quota.used)}
          sub={`of ${quota.quota} used`}
          tone={quota.over ? 'warn' : 'default'}
        />
      </div>

      {catalogError && <Note tone="warn">{catalogError}</Note>}

      {/* Panel tabs -------------------------------------------------------- */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
        {PANEL_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => onPanelChange(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap min-h-[44px] ${
              panel === t.id
                ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-500/40'
                : 'text-stone-500 border border-stone-300 hover:text-stone-900 hover:bg-stone-200/60'
            }`}
          >
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {panel === 'plan' && (
        <PlanPanel
          tenantId={tenantId}
          tenant={tenant}
          locations={locations}
          prices={prices}
          skus={skus}
          entitlements={entitlements}
          onReload={onReload}
          notify={notify}
        />
      )}

      {panel === 'locations' && (
        <LocationsPanel
          tenantId={tenantId}
          locations={locations}
          loading={locationsLoading}
          units={units}
          modelNames={modelNames}
          prices={prices}
          perStoreSkus={perStoreSkus}
          // The whole catalog, not just the per-store SKUs: "Change price" opens
          // the plan editor scoped to one store, and that has to be able to add
          // a tenant-scoped package as well as a per-store one.
          skus={skus}
          onReloadLocations={onReloadLocations}
          notify={notify}
        />
      )}

      {panel === 'devices' && (
        <DevicesPanel
          units={units}
          orders={orders}
          modelNames={modelNames}
          locationNames={locationNames}
          loading={hardwareLoading}
          unitsError={unitsError}
          ordersError={ordersError}
          onReload={loadHardware}
          notify={notify}
        />
      )}

      {panel === 'history' && (
        <HistoryPanel
          tenantId={tenantId}
          billingEvents={billingEvents}
          units={units}
          hardwareError={unitsError}
          hardwareLoading={hardwareLoading}
          modelNames={modelNames}
          onReload={onReload}
          notify={notify}
        />
      )}
    </div>
  );
};

export default TenantFinancePanels;
